/**
 * The Design Builder's API map, loaded from
 * `assets/self-service-config/design-builder.json`.
 *
 * Two backends, two jobs:
 *
 * - **shape builder** — the design TEMPLATE store. A template is the screen
 *   plus its data mapping (dataset, field, aggregation). Never rows.
 * - **platform** — the published DASHBOARD store (`dashboard_layouts`), the
 *   same table the DVM Layout Builder publishes to.
 *
 * Endpoints live in config rather than in code so a deployment can repoint one
 * without a rebuild, which is how every other builder here already works. The
 * defaults below are what ships, so nothing breaks if the file is missing.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { catchError, of, tap } from 'rxjs';

import { SHAPE_BUILDER_BASE_URL } from '../design-builder.constants';
import { buildSelfServiceApiUrl, resolveSelfServiceApiUrl } from '../../shared/self-service-api.utils';

export interface DesignApiEndpoints {
  /** Shape builder: the design template store — the LIST / open side. */
  templatesApi: string;
  /**
   * Platform: where saved designs are read back from. The collection URL;
   * one design is `<readApi>/<dashboardId>`.
   */
  readApi: string;
  /**
   * Platform: where Save writes the design, layers and all. Resolves against
   * the self-service base URL, not the shape-builder host.
   */
  saveApi: string;
  /** Platform: published dashboards. Blank = the shared dashboard service. */
  publishApi: string;
  publishEntity: string;
  /** Groups dashboards; falls back to localStorage when the backend lacks it. */
  projectsApi: string;
}

export interface DesignApiConfig {
  apiEndpoints: DesignApiEndpoints;
  storageKeys: { designs: string; draft: string; projects: string };
  publish: { builderTag: string };
}

const DEFAULTS: DesignApiConfig = {
  apiEndpoints: {
    templatesApi: '/api/shape-builder-components',
    readApi: '/spriced/platform/api/dashboard-layers',
    saveApi: '/spriced/platform/api/dashboard-layers',
    publishApi: '',
    publishEntity: 'dashboard_layouts',
    projectsApi: '/api/dvm/self-service/design-projects',
  },
  storageKeys: {
    designs: 'dvm.selfService.designs',
    draft: 'dvm.selfService.designDraft',
    projects: 'dvm.selfService.designProjects',
  },
  publish: { builderTag: 'design' },
};

const CONFIG_URL = '/assets/self-service-config/design-builder.json';

@Injectable({ providedIn: 'root' })
export class DesignApiConfigService {
  private readonly http = inject(HttpClient);
  private readonly config = signal<DesignApiConfig>(DEFAULTS);

  readonly endpoints = () => this.config().apiEndpoints;
  readonly storageKeys = () => this.config().storageKeys;
  readonly publishOptions = () => this.config().publish;

  /** Load once at startup; the defaults stand if the file is absent. */
  load(): void {
    this.http
      .get<Partial<DesignApiConfig>>(CONFIG_URL)
      .pipe(
        tap((raw) => this.config.set(merge(raw))),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  /**
   * The template store lives on the shape-builder backend, which is a separate
   * host from the self-service base — so these resolve against `environment.URL`
   * rather than the usual helper.
   */
  templatesUrl(): string {
    return absolute(this.endpoints().templatesApi);
  }

  /**
   * Where Save posts the design. On the platform host (the same base every
   * other self-service call uses), so an absolute URL in the config wins and a
   * path hangs off `apiEndpoints.baseUrl`.
   */
  saveUrl(): string {
    return resolveSelfServiceApiUrl(this.endpoints().saveApi, DEFAULTS.apiEndpoints.saveApi);
  }

  /** Where saved designs are read back from. Append `/<dashboardId>` for one. */
  readUrl(): string {
    return resolveSelfServiceApiUrl(this.endpoints().readApi, DEFAULTS.apiEndpoints.readApi);
  }

  /** Projects sit on the self-service base URL, not the shape-builder host. */
  projectsUrl(): string {
    return buildSelfServiceApiUrl(this.endpoints().projectsApi);
  }
}

/** Shallow per-section merge, so a partial config overrides only what it names. */
function merge(raw: Partial<DesignApiConfig> | null): DesignApiConfig {
  if (!raw) return DEFAULTS;
  return {
    apiEndpoints: { ...DEFAULTS.apiEndpoints, ...(raw.apiEndpoints ?? {}) },
    storageKeys: { ...DEFAULTS.storageKeys, ...(raw.storageKeys ?? {}) },
    publish: { ...DEFAULTS.publish, ...(raw.publish ?? {}) },
  };
}

/** An absolute URL is used as given; a path hangs off the shape-builder host. */
function absolute(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SHAPE_BUILDER_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}
